import { useState } from "react"
import { Link, router } from "@inertiajs/react"
import { ArrowRight, PenLine } from "lucide-react"

import DiscardDraftDialog from "@/components/tuw/DiscardDraftDialog"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { discardDraft } from "@/lib/postProjectActions"
import { cn } from "@/lib/utils"

// Resume-your-draft banner (Atrium Phase 3, ADR-014 · Tight).
//
// Design ref: S-04 customer dashboard, #resumeBanner / renderResume() in
// design-handoff/spec/dashboard/customer-dashboard-light.html.
//
// Visibility is owned by draft PRESENCE, not the dashboard variant: it renders
// whenever the customer has a single active draft, on BOTH the new and returning
// variants (hidden when `draft` is null). On the "new" variant it takes the
// louder `.midbuild` treatment — a mid-builder (zero live projects + an
// unfinished draft) gets pulled back into their draft instead of being told to
// "post your first project"; on "returning" it's the quiet "pick up where you
// left off" nudge.
//
// The banner shows the customer's most-recent draft. When they hold more than
// one, `moreCount` (= draftCount - 1) drives a "+N more drafts" link into the
// Post-a-Project split entry (S-POST-ENTRY, state E1); it stays dormant for a
// single draft. The banner's own discard runs through the shared
// DiscardDraftDialog — no window.confirm (tuw-app:P-010).
export default function ResumeDraftBanner({ draft, variant = "returning", moreCount = 0 }) {
  // The dialog is controlled by draft presence; the banner holds exactly one
  // draft, so open-state reduces to a boolean feeding `draft={discarding ? draft : null}`.
  const [discarding, setDiscarding] = useState(false)

  if (!draft) return null

  const midbuild = variant === "new"

  const heading = midbuild
    ? "Finish setting up your project"
    : "Pick up where you left off"

  // Confirming in the DS dialog reuses the wizard's own discard path
  // (postProjectActions.discardDraft → DELETE /post/drafts/:id). On finish we
  // reload the dashboard props so the draft prop clears and the banner disappears.
  const confirmDiscard = () => {
    discardDraft(draft.id, {
      onFinish: () => router.reload({ only: ["dashboard", "navCounts"] }),
    })
    setDiscarding(false)
  }

  return (
    <div
      data-testid="resume-banner"
      className={cn(
        // MOBILE-PLAN :47 — "the announce/upsell/resume banners wrap at ≤560".
        // Prototype `.resume{flex-wrap:wrap}` (customer-dashboard-light.html
        // @media max-width:560). Tailwind v4 compiles `max-[N]` EXCLUSIVE, so
        // the ruled ≤560 edge is the literal 561. Until the row wraps, the two
        // `shrink-0` buttons eat the whole line and crush the text block to 0px
        // while its copy spills out underneath them.
        "flex items-center gap-4 rounded-2xl border px-5 py-4 max-[561px]:flex-wrap",
        midbuild
          ? "border-[color-mix(in_oklab,var(--primary)_34%,transparent)] bg-[linear-gradient(100deg,color-mix(in_oklab,var(--primary)_12%,transparent),color-mix(in_oklab,var(--primary)_3%,transparent))]"
          : "border-[color-mix(in_oklab,var(--primary)_22%,transparent)] bg-[linear-gradient(100deg,color-mix(in_oklab,var(--primary)_7%,transparent),color-mix(in_oklab,var(--primary)_2%,transparent))]"
      )}
    >
      <div className="grid size-[42px] shrink-0 place-items-center rounded-[11px] border border-border bg-card">
        <PenLine className="size-5 text-primary" strokeWidth={1.9} />
      </div>

      {/* `.resume .tx{flex:1 1 calc(100% - 60px)}` — once the row wraps the text
          block claims the rest of line one (the 42px icon + the 16px gap = 58px
          sit beside it) instead of shrinking to nothing. */}
      <div
        data-testid="resume-text"
        className="min-w-0 flex-1 max-[561px]:flex-[1_1_calc(100%_-_60px)]"
      >
        <h3 className="font-display text-[14.5px] font-bold">
          {midbuild && (
            <span className="mr-2 inline-block rounded-[5px] bg-primary/10 px-1.5 py-0.5 align-middle text-[10px] font-bold tracking-[0.04em] text-primary uppercase">
              Draft
            </span>
          )}
          {heading}
        </h3>

        <p className="mt-0.5 text-[12.5px] text-muted-foreground">
          {midbuild ? (
            <>You started “{draft.name}” but haven’t posted it yet.</>
          ) : (
            <>
              “{draft.name}” · {draft.typeLabel} ·{" "}
              {draft.editedLabel?.toLowerCase()}
            </>
          )}
        </p>

        {/* `.resume .pillbar{flex-wrap:wrap}` — the step label, the 120px bar and
            the "+N more drafts" link get a second line rather than squeezing. */}
        <div className="mt-2 flex items-center gap-2 max-[561px]:flex-wrap">
          <span className="text-[11px] font-semibold text-ink-3">
            Step {draft.stepNum} of {draft.stepTotal} · {draft.stepLabel}
          </span>
          <Progress value={draft.pct} className="h-[5px] w-[120px]" />
          {moreCount > 0 && (
            <Link
              href="/post"
              className="ml-1 inline-flex items-center gap-1 text-[11.5px] font-semibold text-primary hover:underline"
            >
              +{moreCount} more draft{moreCount === 1 ? "" : "s"}
              <ArrowRight className="size-3.5" strokeWidth={2.2} />
            </Link>
          )}
        </div>
      </div>

      {/* `.resume .btns{width:100%;justify-content:flex-end}` — the cluster drops
          to its own full-width line and stays right-aligned. */}
      <div
        data-testid="resume-buttons"
        className="flex shrink-0 gap-2 max-[561px]:w-full max-[561px]:justify-end"
      >
        <Button type="button" variant="outline" size="sm" onClick={() => setDiscarding(true)}>
          Discard
        </Button>
        <Button asChild size="sm">
          <Link href={draft.resumeRoute}>
            Resume draft <ArrowRight className="size-4" strokeWidth={2.2} />
          </Link>
        </Button>
      </div>

      <DiscardDraftDialog
        draft={discarding ? draft : null}
        onOpenChange={() => setDiscarding(false)}
        onConfirm={confirmDiscard}
      />
    </div>
  )
}
